import { Controller } from "@hotwired/stimulus"
import { changerIcone } from "../lib/icones"

export default class extends Controller {
    static targets = ["source", "button", "label", "icon"]
    static classes = ["success", "normal"]
    static values  = { text: String }

    // Usage:
    // Old form (kept for back-compat):
    //   <div data-controller="clipboard">
    //     <input data-clipboard-target="source" ...>
    //     <button data-action="clipboard#copy" data-clipboard-target="button">Copy</button>
    //   </div>
    //
    // Simpler form (preferred):
    //   <button data-controller="clipboard"
    //           data-clipboard-text-value="https://…"
    //           data-action="click->clipboard#copy">
    //     <span data-clipboard-target="label">Copy link</span>
    //   </button>

    copy(event) {
        if (event) event.preventDefault()

        let text = this.textValue
        if (!text && this.hasSourceTarget) {
            text = this.sourceTarget.value || this.sourceTarget.textContent.trim()
        }
        if (!text) return

        navigator.clipboard.writeText(text).then(() => {
            this.success()
        }).catch(err => {
            console.error("Clipboard failed, trying execCommand fallback", err)
            if (this.hasSourceTarget) this.fallbackCopy(text)
        })
    }

    fallbackCopy(text) {
        try {
            this.sourceTarget.select()
            document.execCommand("copy")
            this.success()
        } catch (err) {
            console.error("Fallback copy failed", err)
            alert("Copie automatique impossible. Copiez le lien à la main.")
        }
    }

    success() {
        // Lightweight feedback when used in label/icon form — flip the label
        // text or the icon class for ~1.5s. Skip the legacy innerHTML rewrite
        // path entirely in that case.
        if (this.hasLabelTarget || this.hasIconTarget) {
            if (this.hasLabelTarget) {
                if (this._origLabel == null) this._origLabel = this.labelTarget.textContent
                this.labelTarget.textContent = "Copié !"
            }
            if (this.hasIconTarget) {
                // Icône Lucide : on garde ses classes et son dessin pour les remettre.
                if (this._origIcon == null) this._origIcon = { cls: this.iconTarget.getAttribute("class"), html: this.iconTarget.innerHTML }
                changerIcone(this.iconTarget, "check")
            }
            setTimeout(() => {
                if (this.hasLabelTarget && this._origLabel != null) this.labelTarget.textContent = this._origLabel
                if (this.hasIconTarget && this._origIcon != null) {
                    this.iconTarget.setAttribute("class", this._origIcon.cls)
                    this.iconTarget.innerHTML = this._origIcon.html
                }
                this._origLabel = this._origIcon = null
            }, 1500)
            return
        }

        if (!this.hasButtonTarget) return
        const btn = this.buttonTarget

        // Save original state if not saved
        if (!this.originalContent) {
            this.originalContent = btn.innerHTML
            this.originalClasses = [...btn.classList]
        }

        // Update Content
        btn.innerHTML = `
      <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
      <span>Copied!</span>
    `

        // Apply Success Classes (and optionally remove Normal classes if provided)
        if (this.hasNormalClass) {
            this.buttonTarget.classList.remove(...this.normalClasses)
        }
        if (this.hasSuccessClass) {
            this.buttonTarget.classList.add(...this.successClasses)
        }

        // Revert after 2s
        setTimeout(() => {
            this.revert()
        }, 2000)
    }

    revert() {
        const btn = this.buttonTarget
        btn.innerHTML = this.originalContent

        if (this.hasSuccessClass) {
            this.buttonTarget.classList.remove(...this.successClasses)
        }
        // Restore original classes (or normal classes)
        if (this.hasNormalClass) {
            this.buttonTarget.classList.add(...this.normalClasses)
        } else {
            // Fallback restoration if we just messed with classes
            // Note: Adding original classes back might duplicate if we didn't remove them. 
            // Safer to just remove success classes.
        }
    }
}
